import {consume} from "@lit/context";
import {t} from "i18next";
import type {PropertyValues} from "lit";
import {LitElement, html, nothing, unsafeCSS} from "lit";
import {customElement, state} from "lit/decorators.js";
import {classMap} from "lit/directives/class-map.js";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {html as staticHtml, unsafeStatic} from "lit/static-html.js";
import type {RuntimeEnvironment} from "@common-core/web-component-runtime/runtime";
import SignOutIcon from "@interstate/icons-svg/arrow-right-on-rectangle.svg?raw";
import InformationCircleIcon from "@interstate/icons-svg/information-circle.svg?raw";
import ExclamationTriangleIcon from "@interstate/icons-svg/exclamation-triangle.svg?raw";
import {COLLAPSE_COMPONENTS_EVENT, ROOT_TAG} from "../../globals";
import {BridgeBarDataContext} from "../../backend";
import type {
  BridgeBarDataContextValue,
  BridgeUser,
  DisplayInfo,
  UserLink
} from "../../backend";
import {BridgeBarConfigContext} from "../../config";
import type {
  BridgeBarConfig,
  ConfigUserLink,
  UserDataEntry
} from "../../config";
import {RuntimeContext} from "../../runtime";
import type {Environment} from "../../runtime";
import {InterstateIcon, resolveInterstateIcon} from "../../interstate";
import {sanitizeHtml} from "../../security";
import {emitUserMenuToggle, emitUserMenuNav} from "../../analytics";
import styles from "./UserProfileMenuElement.css?inline";

/** Custom element tag name, following S-7 naming convention */
const tagName = `${ROOT_TAG}-user-profile-menu`;

/** Exported static reference for use in Lit html templates */
export const UserProfileMenu = unsafeStatic(tagName);

/**
 * Interstate DIY classes: row-styled action link inside the listbox. The
 * plugin's `.ids-listbox-action` resolves the text color to the cobalt
 * interactive token for light mode; in dark mode the legacy bridge bar
 * inverts these labels to white (reference: new-user-menu-open-interstate-dark),
 * so we override with `dark:!text-white` to beat the plugin's CSS var.
 */
const MENU_LINK_CLASSES =
  "menu-link ids-listbox-item ids-listbox-action dark:!text-white dark:hover:!bg-primary-600";

/**
 * Converts a legacy BB-API link `mode` into the equivalent HTML anchor
 * `target` value. Mirrors the 4-case mapping from the original Bridge Bar
 * UI's convertLinkMode so migrating solutions get the same window-targeting
 * behavior. Unknown / missing modes fall back to `_self`.
 */
function convertLinkMode(mode: string | undefined): string {
  switch (mode) {
    case "NEW_WINDOW":
      return "_blank";
    case "PARENT_WINDOW":
      return "_parent";
    case "TOP_WINDOW":
      return "_top";
    case "CURRENT_WINDOW":
      return "_self";
    default:
      return "_self";
  }
}

/**
 * UserProfileMenuElement renders the user profile menu for the Bridge Bar.
 *
 * Consumes BridgeBarDataContext for user info, BridgeBarConfigContext for
 * solution-provided extras, and RuntimeContext for the sign-out URL template.
 * Identity entries, menu links, and the sign-out destination are all driven
 * from those contexts — no hardcoded labels or URLs.
 */
@customElement(tagName)
export class UserProfileMenuElement extends LitElement {
  static styles = unsafeCSS(styles);

  /** Consumed data context — provides user details or error status */
  @consume({context: BridgeBarDataContext, subscribe: true})
  private data: BridgeBarDataContextValue = {status: "pending"};

  /** Consumed config context — provides solution-level extras (userLinks, userData) */
  @consume({context: BridgeBarConfigContext, subscribe: true})
  private config: BridgeBarConfig = {
    applicationId: "",
    sessionId: "",
    deployment: "live",
    partition: "prod"
  };

  /** Consumed runtime context — provides environment-specific URLs (including logoutUrl) */
  @consume({context: RuntimeContext, subscribe: true})
  private runtime?: RuntimeEnvironment<Environment, unknown>;

  /** Whether the profile menu flyout is currently open */
  @state()
  private menuOpen = false;

  /** Bound reference to the document click handler for outside-click detection */
  private handleDocumentClick: (e: MouseEvent) => void;

  /** Bound reference to the document keydown handler for Escape key detection */
  private handleDocumentKeyDown: (e: KeyboardEvent) => void;

  /** Bound reference to the container's collapse-all broadcast handler */
  private handleCollapseRequest: () => void;

  constructor() {
    super();
    // Bind handlers once so the same reference can be added and removed
    this.handleDocumentClick = (e: MouseEvent) => {
      if (!e.composedPath().includes(this)) {
        this.closeMenu();
      }
    };
    // Close the menu on Escape regardless of which element has focus
    this.handleDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && this.menuOpen) {
        this.closeMenu();
      }
    };
    // Collapse when the container broadcasts a collapse-all request.
    this.handleCollapseRequest = () => {
      this.closeMenu();
    };
  }

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleDocumentKeyDown);
    document.addEventListener(
      COLLAPSE_COMPONENTS_EVENT,
      this.handleCollapseRequest
    );
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("keydown", this.handleDocumentKeyDown);
    document.removeEventListener(
      COLLAPSE_COMPONENTS_EVENT,
      this.handleCollapseRequest
    );
  }

  /**
   * Reflect the consumed theme onto a host attribute so Tailwind `dark:`
   * utilities (scoped to `:host([data-ids-theme-mode="dark"])`) apply inside
   * this shadow root. Runs in `willUpdate` so the attribute is set *before*
   * the first render — otherwise the light-mode styles would flash on initial
   * paint. `:host-context()` is not supported in Firefox, so each themed
   * element must carry the attribute on its own host.
   */
  override willUpdate(_changed: PropertyValues) {
    const mode = this.config.theme ?? "light";
    // Guard: avoid thrashing the DOM attribute when the resolved mode hasn't changed.
    if (this.getAttribute("data-ids-theme-mode") !== mode) {
      this.setAttribute("data-ids-theme-mode", mode);
    }
  }

  private getInitials(givenName: string, familyName: string): string {
    const first = givenName.trim()[0] ?? "";
    const last = familyName.trim()[0] ?? "";
    return (first + last).toUpperCase();
  }

  private openMenu() {
    this.menuOpen = true;
    this.dispatchEvent(
      new CustomEvent("component-expanded", {
        detail: {component: "user-profile-menu"},
        composed: true,
        bubbles: true
      })
    );
  }

  private closeMenu() {
    if (!this.menuOpen) return;
    this.menuOpen = false;
    this.dispatchEvent(
      new CustomEvent("component-collapsed", {
        detail: {component: "user-profile-menu"},
        composed: true,
        bubbles: true
      })
    );
  }

  private handleAvatarClick(e: MouseEvent) {
    if (this.menuOpen) {
      emitUserMenuToggle("collapsed", e, this.config?.getTracker);
      this.closeMenu();
    } else {
      emitUserMenuToggle("expanded", e, this.config?.getTracker);
      this.openMenu();
    }
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      this.closeMenu();
    }
  }

  /**
   * Build the sign-out URL from the runtime context's logoutUrl template,
   * substituting `{solution}` with the current applicationId from config.
   */
  private signOutUrl(): string {
    const template = this.runtime?.environment?.logoutUrl ?? "";
    return template.replace("{solution}", this.config.applicationId ?? "");
  }

  /**
   * Render an Interstate icon before a menu link's label. The glyph inherits
   * the link's `currentColor`, which is driven by `.ids-listbox-action` so
   * icon and label stay color-synchronized across rest/hover/dark states.
   */
  private renderMenuIcon(glyph: string | null) {
    if (!glyph) return nothing;
    return staticHtml`
      <${InterstateIcon}
        class="ids-icon shrink-0"
        .svg=${glyph}>
      </${InterstateIcon}>
    `;
  }

  /**
   * Render the initials avatar trigger.
   *
   * The clickable element is a full-bar-height (35px) square cell so the
   * hover highlight covers the whole component rather than just the round
   * chip. The circular chip itself is a non-interactive `ids-avatar` span
   * nested inside.
   *
   * The chip uses the Interstate `ids-avatar` base for shape/size/font but
   * skips `ids-avatar-interactive` because that token paints the border with
   * `borderColor.interactive.rest` = `#005ba8` (primary-700), too dark.
   * The legacy bridge-bar uses `$bridgeBarBlue` (`#077dbd`) for both the
   * ring and the initials, on a white fill, in BOTH themes (the dark-mode
   * bar sits behind a still-white chip — no dark: overrides needed). The
   * closest Interstate preset to `#077dbd` is `blue-500` (`#318ad3`);
   * a sampled ring pixel from the reference PNG came out at `#3193c9`,
   * which matches blue-500 almost exactly, so that's what we use.
   *
   * Hover paints the square cell (blue-50 light / primary-600 dark) so the
   * highlight matches the selected-row treatment elsewhere in the bar.
   */
  private renderAvatar(initials: string) {
    if (!initials) return nothing;
    return html`
      <button
        class="profile-avatar flex items-center justify-center h-[35px] w-[35px] rounded-md bg-transparent border-0 p-0 hover:bg-blue-50 dark:hover:bg-primary-600 cursor-pointer font-bridge"
        aria-label=${t("profileMenu.ariaLabel")}
        aria-expanded=${this.menuOpen}
        @click=${this.handleAvatarClick}
        @contextmenu=${this.handleAvatarClick}
        @keydown=${this.handleKeyDown}>
        <span
          class="ids-avatar bg-white border border-solid border-blue-500 text-blue-500"
          data-ids-size="xs">
          <span class="ids-avatar-initials">${initials}</span>
        </span>
      </button>
    `;
  }
  private renderPendingAvatar() {
    return staticHtml`
      <button
        class="profile-avatar flex items-center justify-center h-[35px] w-[35px] rounded-md bg-transparent border-0 p-0 hover:bg-blue-50 dark:hover:bg-primary-600 cursor-pointer font-bridge"
        aria-label=${t("profileMenu.ariaLabel")}
        aria-expanded=${this.menuOpen}
        @click=${this.handleAvatarClick}
        @contextmenu=${this.handleAvatarClick}
        @keydown=${this.handleKeyDown}>
            <span class="ids-spinner" role="progressbar" aria-label="Loading" style="--ids-comp-spinner-size: 20px;">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
                    <circle cx="50" cy="50" r="40" stroke="var(--ids-comp-spinner-ring-color)" stroke-width="var(--ids-comp-spinner-ring-width)" stroke-linecap="var(--ids-comp-spinner-ring-linecap)" fill="var(--ids-comp-spinner-center-color)">
                        <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="1.5384615384615383s" values="0 50 50;180 50 50;720 50 50" keyTimes="0;0.5;1">
                        </animateTransform>
                        <animate attributeName="stroke-dasharray" repeatCount="indefinite" dur="1.5384615384615383s" values="12.566370614359172 238.76104167282426;188.4955592153876 62.83185307179585;12.566370614359172 238.76104167282426" keyTimes="0;0.5;1">
                        </animate>
                    </circle>
                </svg>
            </span>
      </button>
    `;
  }

  private renderErrorAvatar() {
    return staticHtml`
      <button
        class="profile-avatar flex items-center justify-center h-[35px] w-[35px] rounded-md bg-transparent border-0 p-0 hover:bg-blue-50 dark:hover:bg-primary-600 cursor-pointer font-bridge"
        aria-label=${t("profileMenu.ariaLabel")}
        aria-expanded=${this.menuOpen}
        @click=${this.handleAvatarClick}
        @contextmenu=${this.handleAvatarClick}
        @keydown=${this.handleKeyDown}>
            <${InterstateIcon}
                svg="${ExclamationTriangleIcon}"
                class="flex-shrink-0 text-2xl text-amber-400"
               ></${InterstateIcon}>
      </button>
    `;
  }
  /**
   * Render the identity block from the combined `display_info` (API) and
   * `userData` (solution config) lists. Each entry is a header/value pair.
   */
  private renderIdentity(
    displayInfo: DisplayInfo[],
    userData: UserDataEntry[]
  ) {
    const entries: {header: string; body: string}[] = [
      ...displayInfo,
      ...userData.map(d => ({header: d.key, body: d.value}))
    ];
    if (entries.length === 0) return nothing;
    // TODO: Investigate real use cases of userData to determine if html characters are used: unsafeHtml(santizieHtml)).
    return html`
      <div
        class="profile-identity px-4 py-3 border-b border-muted dark:border-primary-700 font-bridge">
        ${entries.map(
          (entry, i) => html`
            <span
              class=${classMap({
                "identity-label": true,
                "block": true,
                "text-sm": true,
                "font-semibold": true,
                "text-gray-700": true,
                "dark:text-gray-300": true,
                "mt-2": i > 0
              })}
              >${entry.header}</span
            >
            <span
              class="identity-value block text-base font-normal break-all text-gray-800 dark:text-white"
              >${entry.body}</span
            >
          `
        )}
      </div>
    `;
  }

  /**
   * Render the combined user-menu link list.
   *
   * Ordering matches the legacy Bridge Bar: solution-provided config links
   * render **before** API-provided `user_links` (legacy's extraUserLinks
   * section sat above the standard userLinks section).
   *
   * Config links honor three optional fields carried by `ConfigUserLink`:
   * - `callback` — when set, clicking invokes the callback and blocks
   *   navigation (href is set to "#"). Mirrors legacy `addLink` behavior.
   * - `className` — applied as an extra class on the `<a>` so solutions
   *   can theme individual items without overriding base DIY classes, and
   *   used as an icon-token lookup (e.g. "my-settings" → cog glyph).
   * - `target` — defaults to `_top` (legacy default) to break out of any
   *   iframe the bridge bar is embedded inside.
   *
   * API links use `convertLinkMode` to translate the backend `mode` enum
   * (NEW_WINDOW / PARENT_WINDOW / TOP_WINDOW / CURRENT_WINDOW) into an
   * anchor `target`, and their `icon` field to pick a glyph.
   */
  private renderUserLinks(apiLinks: UserLink[], configLinks: ConfigUserLink[]) {
    const apiUserLinks = apiLinks.filter(l => l.menu === "user");
    return html`
      ${configLinks.map(link => {
        const hasCallback = typeof link.callback === "function";
        const handleClick = (e: MouseEvent) => {
          emitUserMenuNav(
            {label: link.label, hasCallback},
            e,
            this.config?.getTracker
          );
          // Only run the callback on left-click. Right-click (contextmenu)
          // records the analytics event but must leave the browser's native
          // context menu intact, matching legacy userMenu.js behavior.
          if (hasCallback && e.type === "click") {
            e.preventDefault();
            link.callback?.();
          }
        };
        const linkClasses: Record<string, boolean> = {
          "menu-link": true,
          "custom-user-link": true,
          "ids-listbox-item": true,
          "ids-listbox-action": true,
          "dark:!text-white": true,
          "dark:hover:!bg-primary-600": true
        };
        if (link.className) {
          linkClasses[link.className] = true;
        }
        return html`
          <a
            class=${classMap(linkClasses)}
            href=${hasCallback ? "#" : link.url}
            target=${link.target ?? "_top"}
            @click=${handleClick}
            @contextmenu=${handleClick}>
            ${this.renderMenuIcon(resolveInterstateIcon(link.className))}
            <span>${unsafeHTML(sanitizeHtml(link.label))}</span>
          </a>
        `;
      })}
      ${apiUserLinks.map(link => {
        const handleApiLinkClick = (e: MouseEvent) => {
          emitUserMenuNav(
            {label: link.label, hasCallback: false},
            e,
            this.config?.getTracker
          );
        };
        return html`
          <a
            class="${MENU_LINK_CLASSES} api-user-link"
            href=${link.uri}
            target=${convertLinkMode(link.mode)}
            @click=${handleApiLinkClick}
            @contextmenu=${handleApiLinkClick}>
            ${this.renderMenuIcon(resolveInterstateIcon(link.icon))}
            <span>${unsafeHTML(sanitizeHtml(link.label))}</span>
          </a>
        `;
      })}
    `;
  }

  /**
   * Render the full menu flyout for a successfully loaded user (AC-3.2 through AC-3.8).
   * Identity entries and menu links are data-driven; sign-out URL comes from runtime.
   */
  private renderMenu(data: BridgeUser) {
    if (!this.menuOpen) return nothing;
    const configUserLinks = this.config.userLinks ?? [];
    const configUserData = this.config.userData ?? [];
    const handleSignOutClick = (e: MouseEvent) => {
      emitUserMenuNav(
        {label: t("profileMenu.signOut"), hasCallback: false},
        e,
        this.config?.getTracker
      );
    };
    return html`
      <div
        class="profile-menu ids-popover absolute top-full right-0 mt-2 z-50 min-w-56 bg-app dark:bg-primary-900 font-bridge"
        data-ids-variant="compact"
        role="menu"
        open>
        ${this.renderIdentity(data.display_info ?? [], configUserData)}
        <div class="ids-listbox w-full">
          <div class="ids-listbox-list flex flex-col">
            ${this.renderUserLinks(data.user_links ?? [], configUserLinks)}
            <a
              class="${MENU_LINK_CLASSES} sign-out-link"
              href=${this.signOutUrl()}
              @click=${handleSignOutClick}
              @contextmenu=${handleSignOutClick}>
              ${this.renderMenuIcon(SignOutIcon)}
              <span>${unsafeHTML(t("profileMenu.signOut"))}</span>
            </a>
          </div>
        </div>
      </div>
    `;
  }

  private renderErrorState() {
    if (!this.menuOpen) return nothing;
    const handleSignOutClick = (e: MouseEvent) => {
      emitUserMenuNav(
        {label: t("profileMenu.signOut"), hasCallback: false},
        e,
        this.config?.getTracker
      );
    };
    return staticHtml`
      <div
        class="profile-menu ids-popover absolute top-full right-0 mt-2 z-50 min-w-56 bg-app dark:bg-primary-900 font-bridge"
        data-ids-variant="compact"
        open>
        <div class="ids-alert" data-ids-variant="caution">
            <span class="ids-alert-icon">
                <${InterstateIcon}
                  svg="${ExclamationTriangleIcon}"
                  class="flex-shrink-0"
                ></${InterstateIcon}>
            </span>
          <div class="ids-alert-body">
                <span class="ids-alert-message">
                    ${unsafeHTML(t("profileMenu.errorMessage"))}
                </span>
          </div>
        </div>
        <div class="ids-listbox w-full">
          <div class="ids-listbox-list flex flex-col">
            <a
              class="${MENU_LINK_CLASSES} sign-out-link"
              href=${this.signOutUrl()}
              @click=${handleSignOutClick}
              @contextmenu=${handleSignOutClick}>
              ${this.renderMenuIcon(SignOutIcon)}
              <span>${unsafeHTML(t("profileMenu.signOut"))}</span>
            </a>
          </div>
        </div>
      </div>
    `;
  }
  private renderPendingState() {
    if (!this.menuOpen) return nothing;
    const handleSignOutClick = (e: MouseEvent) => {
      emitUserMenuNav(
        {label: t("profileMenu.signOut"), hasCallback: false},
        e,
        this.config?.getTracker
      );
    };
    return staticHtml`
      <div
        class="profile-menu ids-popover absolute top-full right-0 mt-2 z-50 min-w-56 bg-app dark:bg-primary-900 font-bridge"
        data-ids-variant="compact"
        open>
        <div class="ids-alert" data-ids-variant="info">
            <span class="ids-alert-icon">
                <${InterstateIcon}
                    svg="${InformationCircleIcon}"
                    class="flex-shrink-0"
                   ></${InterstateIcon}>
            </span>
            <div class="ids-alert-body">
                <span class="ids-alert-message">
                    ${unsafeHTML(t("profileMenu.loadingMessage"))}
                </span>
            </div>
        </div>
        <div class="ids-listbox w-full">
          <div class="ids-listbox-list flex flex-col">
            <a
              class="${MENU_LINK_CLASSES} sign-out-link"
              href=${this.signOutUrl()}
              @click=${handleSignOutClick}
              @contextmenu=${handleSignOutClick}>
              ${this.renderMenuIcon(SignOutIcon)}
              <span>${unsafeHTML(t("profileMenu.signOut"))}</span>
            </a>
          </div>
        </div>
      </div>
    `;
  }
  override render() {
    const ctx = this.data;

    // Error state — always show warning and sign out
    if (ctx.status === "error") {
      return html` ${this.renderErrorAvatar()} ${this.renderErrorState()}`;
    }
    if (ctx.status === "pending") {
      return html` ${this.renderPendingAvatar()} ${this.renderPendingState()}`;
    }
    // complete — derive initials and render avatar + optional menu
    const initials =
      ctx.status === "complete"
        ? this.getInitials(ctx.data.given_name, ctx.data.family_name)
        : "";

    return html`
      ${this.renderAvatar(initials)}
      ${ctx.status === "complete" ? this.renderMenu(ctx.data) : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: UserProfileMenuElement;
  }
}
